import React from 'react';
import { Tooltip } from 'antd';
import { SunOutlined, MoonOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { styled, keyframes, css } from 'styled-components';

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const StyledThemeToggle = styled.button`
  position: relative;
  width: 40px;
  height: 40px;
  background: ${props => props.isDarkMode 
    ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' 
    : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
  };
  border: 2px solid ${props => props.isDarkMode ? '#475569' : '#cbd5e1'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.isDarkMode 
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)' 
      : 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(239, 68, 68, 0.1) 100%)'
    };
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    border-color: ${props => props.isDarkMode ? '#6366f1' : '#f59e0b'};
    box-shadow: 0 8px 25px ${props => props.isDarkMode 
      ? 'rgba(99, 102, 241, 0.3)' 
      : 'rgba(245, 158, 11, 0.3)'
    };
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 15px ${props => props.isDarkMode 
      ? 'rgba(99, 102, 241, 0.2)' 
      : 'rgba(245, 158, 11, 0.2)'
    };
  }
  
  &:focus {
    box-shadow: 0 0 0 3px ${props => props.isDarkMode 
      ? 'rgba(99, 102, 241, 0.3)' 
      : 'rgba(245, 158, 11, 0.3)'
    };
  }
`;

const IconContainer = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${props => props.isDarkMode ? '#e2e8f0' : '#475569'};
  font-size: 16px;
  transition: all 0.3s ease;
  
  ${props => props.isDarkMode && css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
`;

const SunIconStyled = styled(SunOutlined)`
  ${props => !props.isDarkMode && css`animation: ${rotate} 4s linear infinite;`}
  color: ${props => props.isDarkMode ? '#e2e8f0' : '#f59e0b'};
`;

const MoonIconStyled = styled(MoonOutlined)`
  color: ${props => props.isDarkMode ? '#6366f1' : '#475569'};
`;

const ThemeToggle = () => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <Tooltip title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
      <StyledThemeToggle 
        onClick={toggleTheme}
        isDarkMode={isDarkMode}
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <IconContainer isDarkMode={isDarkMode}>
          {isDarkMode ? (
            <MoonIconStyled />
          ) : (
            <SunIconStyled isDarkMode={isDarkMode} />
          )}
        </IconContainer>
      </StyledThemeToggle>
    </Tooltip>
  );
};

export default ThemeToggle; 